import React, { useState } from "react";
import ConfirmHeader from "./ConfirmHeader";
import { CONFIRM_STACK, CONFIRM_DANGER, CONFIRM_CANCEL } from "../theme/sharedStyles";
import { intervalLabel } from "../utils/habitInterval";
import { isAmountHabit, formatDailyGoal, loggedAmount, formatAmount } from "../utils/habitUnits";
import { goalOn } from "../utils/habitWeeklyGoal";
import {
  Info, Repeat, CalendarDays, Check, Clock, MapPin, Flag, Tag, Paperclip,
  Pencil, X, Timer, Trash2, TrendingUp, StickyNote,
} from "lucide-react";
import { HabitCheckbox } from "./HabitCheckbox";
import { EmbedPill } from "./EmbedPill";
import { CategoryIcon } from "./CategoryIcon";
import { AttachmentViewer } from "./attachments/AttachmentViewer";
import { PRIORITY_COLORS } from "../constants/palettes";
import { sessionsForItemOnDate, logOrdinalLabel, logTimeRangeLabel } from "../utils/sessionGrouping";
import { NoteAddPill, SessionNoteBar } from "./SessionNoteField";
import { primeKeyboard } from "../capacitor/keyboardNow";
import { SessionSheet } from "./SessionSheet";

const WEEKDAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const formatHabitFrequency = (h) => {
  switch (h.frequencyType) {
    case "once": return "Once (today only)";
    case "daily": return "Every day";
    case "interval": return intervalLabel(h);
    case "specific": return (h.days?.length ? h.days.map(i => WEEKDAY_LABELS[i]).join(", ") : "No days picked");
    case "count": return `${h.weeklyCount || 3}x a week`;
    case "monthly": return h.monthlyMode === "count" ? `${h.monthlyCount || 10}x a month` : (h.monthDates?.length ? `Monthly on ${h.monthDates.join(", ")}` : "No dates picked");
    case "yearly": return (h.yearlyMode || "count") === "count" ? `${h.yearlyCount || 12}x a year` : (h.yearDates?.length ? `Yearly on ${h.yearDates.join(", ")}` : "No dates picked");
    default: return "Every day";
  }
};
const formatTaskFrequency = (t) => {
  let base;
  switch (t.frequencyType) {
    case "daily": base = "Every day"; break;
    case "specific": base = (t.frequencyDays?.length ? t.frequencyDays.map(i => WEEKDAY_LABELS[i]).join(", ") : "No days picked"); break;
    case "monthly": base = (t.monthDates?.length ? `Monthly on ${t.monthDates.join(", ")}` : "No dates picked"); break;
    case "yearly": base = (t.yearDates?.length ? `Yearly on ${t.yearDates.join(", ")}` : "No dates picked"); break;
    case "interval": base = intervalLabel(t); break;
    default: return null;
  }
  return t.repeatEnd ? `${base} · until ${t.repeatEnd}` : base;
};

// Shared center pop-up used everywhere a habit/task/to-do's details are shown:
// the "View details" action on the clock-in card, the "Done" pill (with a
// restart choice tucked in), AND clicking a logged session from a category in
// the donut/logged-sessions page — so all three land on the exact same panel.
export function ItemDetailsModal({
  kind, itemRef, categories = [], focusSessions = [], habitLogs = null, targetDate = null,
  hideDuration = false, restart = null, setTasks = () => {}, onClose,
  sessionToDelete = null, onDeleteSession = null, onRenameSession = null,
  onSetSessionNote = null,
  // Fix a log's start/end or delete it (Jade, 2026-10-11): tap its time.
  onEditSessionTimes = null, onRemoveSession = null,
}) {
  const [editingSubtaskId, setEditingSubtaskId] = useState(null);
  const [editingSubtaskText, setEditingSubtaskText] = useState("");
  const [confirmDeleteSession, setConfirmDeleteSession] = useState(false);
  const [editingTitle, setEditingTitle] = useState(false);
  const [noteEditKey, setNoteEditKey] = useState(null); // which log's note is open for editing
  const [timeEditKey, setTimeEditKey] = useState(null); // which log's times are open in the SessionSheet
  const openNoteEdit = (k) => { primeKeyboard(); setNoteEditKey(k); }; // keyboard opens with the tap (keyboardNow.js)
  const [titleDraft, setTitleDraft] = useState("");
  // A logged session can outlive the habit/task/to-do it was logged against
  // (deleted since), or never have had one at all (nothing was assigned when
  // it was clocked in) — itemRef is null in both cases. There's still a
  // session to show details for, so only bail when there's truly nothing:
  // neither a live item nor a logged session to fall back on.
  if (!itemRef && !sessionToDelete) return null;
  const ref = itemRef || {};
  // Was this opened for a session whose habit/task/to-do link is gone?
  const isDeletedLink = !!(sessionToDelete && sessionToDelete.kind && sessionToDelete.refId != null && !itemRef);
  const sessionCat = sessionToDelete
    ? (categories.find(c => c.id === sessionToDelete.categoryId)
        || categories.find(c => c.title === (sessionToDelete.categoryLabel || "").trim()))
    : null;
  const cat = itemRef ? categories.find(c => c.id === ref.goalId) : sessionCat;
  // A past day's unit/goal (a habit's goal can change over time).
  const dayGoal = kind === "habit" && itemRef ? goalOn(ref, targetDate) : ref;

  // A logged session carries its own display label (independent of the
  // linked habit/task/to-do's actual title) — renaming here only edits how
  // that log shows up in Logged Sessions, never the underlying item's name.
  const stripKindPrefix = (lbl) => {
    if (kind && lbl) { const i = lbl.indexOf(": "); if (i >= 0) return lbl.slice(i + 2).trim() || lbl; }
    return lbl?.trim() || null;
  };
  const sessionDisplayTitle = sessionToDelete ? (stripKindPrefix(sessionToDelete.label) || ref.name || ref.title || "Clock-in") : null;
  const displayTitle = sessionToDelete ? sessionDisplayTitle : (ref.name || ref.title);
  const startEditTitle = () => { setTitleDraft(displayTitle || ""); setEditingTitle(true); };
  const saveEditTitle = () => {
    const text = titleDraft.trim();
    if (text && onRenameSession) onRenameSession(text);
    setEditingTitle(false);
  };

  const toggleSubtask = (taskId, subId) => {
    setTasks(prev => prev.map(t => t.id === taskId
      ? { ...t, subtasks: (t.subtasks || []).map(s => s.id === subId ? { ...s, done: !s.done } : s) }
      : t));
  };
  const startEditSubtask = (s) => { setEditingSubtaskId(s.id); setEditingSubtaskText(s.title); };
  const saveEditSubtask = (taskId) => {
    const text = editingSubtaskText.trim();
    if (text) {
      setTasks(prev => prev.map(t => t.id === taskId
        ? { ...t, subtasks: (t.subtasks || []).map(s => s.id === editingSubtaskId ? { ...s, title: text } : s) }
        : t));
    }
    setEditingSubtaskId(null); setEditingSubtaskText("");
  };

  const metaRow = (Icon, label, value) => (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 10, fontSize: 15, color: "var(--c-text-mid)", fontWeight: 600 }}>
      <span style={{ flexShrink: 0, marginTop: 1, color: "var(--c-text-soft)", display: "inline-flex" }}><Icon size={16} /></span>
      <span style={{ wordBreak: "break-word", lineHeight: 1.45 }}>{label ? <><span style={{ color: "var(--c-text-soft)", fontWeight: 700 }}>{label}: </span>{value}</> : value}</span>
    </div>
  );

  // Repeatable-within-a-day duration breakdown — only when we know which day
  // to look at (i.e. opened from a logged session, or from the "Done" pill).
  // When there's no live item (deleted link, or no link at all), match by the
  // session's own refId/label instead of the item's — the item's own id/name
  // no longer exists to match against.
  const matchId = itemRef ? ref.id : (sessionToDelete?.refId ?? null);
  const matchLabel = itemRef ? (ref.name || ref.title) : (sessionToDelete?.label || "");
  // The day's logs — notes need them even when the duration rows are hidden.
  const allDayLogs = (() => {
    if (!targetDate) return [];
    const matched = sessionsForItemOnDate(focusSessions, { kind, id: matchId, label: matchLabel }, targetDate);
    // Fall back to the single session itself if grouping found nothing to
    // match against (e.g. a freeform session logged with no label at all).
    return matched.length > 0 ? matched : (sessionToDelete ? [sessionToDelete] : []);
  })();
  const dayLogs = hideDuration ? [] : allDayLogs;
  // One note per logged session. "Add note" is a small bordered pill in a spot
  // that's already there — the header for a single log, the end of each log
  // line for several — so offering it never adds a row (Jade). A saved note
  // shows as its own row (tap to edit); editing opens a text box in its place.
  const notesOn = !!onSetSessionNote && allDayLogs.length > 0;
  const noteKey = (s) => s.id || s.completedAt || s.startedAt || "";
  const saveNote = (s) => (text) => onSetSessionNote(s, text);
  const singleLog = notesOn && allDayLogs.length === 1 ? allDayLogs[0] : null;
  const noteRow = (s) => s.note ? (
    <div key={`note-${noteKey(s)}`} onClick={() => openNoteEdit(noteKey(s))} role="button" title="Edit note" style={{ cursor: "pointer" }}>
      {metaRow(StickyNote, "Note", <span style={{ whiteSpace: "pre-wrap" }}>{s.note}</span>)}
    </div>
  ) : null;
  // Several logs: the note (or its editor) sits under that log's line.
  const logNote = (s) => {
    if (!notesOn || !s.note) return null;
    return (
      <div onClick={() => openNoteEdit(noteKey(s))} role="button" title="Edit note"
        style={{ marginTop: 1, fontSize: 13, color: "var(--c-text-mid)", fontWeight: 600, whiteSpace: "pre-wrap", wordBreak: "break-word", cursor: "pointer" }}>
        {s.note}
      </div>
    );
  };
  // Editing happens in a bar on top of the keyboard (SessionNoteBar), not in here.
  const editingLogIndex = noteEditKey === null ? -1 : allDayLogs.findIndex(s => noteKey(s) === noteEditKey);
  const editingLog = editingLogIndex >= 0 ? allDayLogs[editingLogIndex] : null;
  const timesOn = !!onEditSessionTimes;
  const timeEditLog = timeEditKey === null ? null : allDayLogs.find(s => noteKey(s) === timeEditKey) || null;
  // A log's time, tappable (with a small pencil) when times can be edited.
  const editableTime = (s, content) => !timesOn ? content : (
    <span role="button" title="Edit time" onClick={() => setTimeEditKey(noteKey(s))}
      style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6 }}>
      {content}<Pencil size={12} style={{ flexShrink: 0, color: "var(--c-text-soft)" }} />
    </span>
  );
  const logNotePill = (s) => notesOn && !s.note && (
    <NoteAddPill small onClick={() => openNoteEdit(noteKey(s))} />
  );
  // One line of a multi-log list: the label/time on the left, the pill on the right.
  const logLine = (s, i, text) => (
    <div key={s.id || i} style={{ fontSize: 13, color: "var(--c-text-mid)", fontWeight: 600 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ flex: 1, minWidth: 0 }}>{text}</span>
        {logNotePill(s)}
      </div>
      {logNote(s)}
    </div>
  );
  const totalMins = dayLogs.reduce((a, s) => a + s.minutes, 0);

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 10054, display: "flex", alignItems: "center", justifyContent: "center", padding: 20, fontFamily: "'Quicksand','Nunito',sans-serif" }}>
      {editingLog && (
        <SessionNoteBar key={noteEditKey} note={editingLog.note || ""}
          label={allDayLogs.length > 1 ? `${logOrdinalLabel(editingLogIndex)} log` : (displayTitle || "")}
          onSave={saveNote(editingLog)} onClose={() => setNoteEditKey(null)} />
      )}
      {timeEditLog && (
        <SessionSheet mode="edit" session={timeEditLog} title={displayTitle || ""}
          onSave={({ start, end, estimated }) => { onEditSessionTimes(timeEditLog, start, end, !!estimated); setTimeEditKey(null); }}
          onDelete={onRemoveSession ? () => { onRemoveSession(timeEditLog); setTimeEditKey(null); } : null}
          onClose={() => setTimeEditKey(null)} />
      )}
      <div className="clarity-fade" style={{ position: "absolute", inset: 0, background: "rgba(40,20,35,0.5)", backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)" }} onClick={onClose} />
      <div className="clarity-pop" style={{ position: "relative", background: "var(--c-bg-card)", borderRadius: 22, width: "min(420px, 92vw)", maxHeight: "85vh", overflowY: "auto", boxShadow: "0 14px 50px rgba(212,120,154,0.28)", padding: "18px 20px 22px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12, gap: 10 }}>
          {editingTitle ? (
            <input autoFocus value={titleDraft} onChange={e => setTitleDraft(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter") saveEditTitle(); if (e.key === "Escape") setEditingTitle(false); }}
              onBlur={saveEditTitle}
              style={{ flex: 1, fontSize: 17, fontWeight: 800, color: "var(--c-text)", border: "1.5px solid var(--c-primary)", borderRadius: 10, padding: "5px 9px", background: "var(--c-bg-card)", fontFamily: "inherit" }} />
          ) : (
            // Tap the title to rename a logged session (Jade, 2026-10-11): no pencil button.
            <span onClick={sessionToDelete && onRenameSession ? startEditTitle : undefined}
              role={sessionToDelete && onRenameSession ? "button" : undefined}
              title={sessionToDelete && onRenameSession ? "Tap to rename this logged session" : undefined}
              style={{ fontSize: 19, fontWeight: 800, color: "var(--c-text)", lineHeight: 1.35, wordBreak: "break-word", flex: 1, minWidth: 0, cursor: sessionToDelete && onRenameSession ? "pointer" : "default" }}>{displayTitle}</span>
          )}
          {/* Note pill + Close stay together, top-aligned, regardless of whether the
              title wraps to a second line. */}
          <span style={{ display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}>
            {!editingTitle && singleLog && !singleLog.note && (
              <span style={{ marginRight: 4 }}><NoteAddPill onClick={() => openNoteEdit(noteKey(singleLog))} /></span>
            )}
            <button onClick={onClose} style={{ background: "var(--c-bg-soft)", border: "none", borderRadius: "50%", width: 30, height: 30, cursor: "pointer", color: "var(--c-text-soft)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><X size={15} /></button>
          </span>
        </div>

        {/* The delete button sits in this row (bottom-aligned), not under it —
            as its own trailing row it left a dead band of empty card below the
            last detail line. alignItems:flex-end keeps it level with whatever
            that last line happens to be. */}
        <div style={{ display: "flex", alignItems: "flex-end", gap: 10 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 9, flex: 1, minWidth: 0 }}>
          {sessionToDelete
            ? metaRow(Tag, "Category", cat ? <><CategoryIcon cat={cat} size={12} /> {cat.title}</> : "Uncategorized")
            : (cat && metaRow(Tag, "Category", <><CategoryIcon cat={cat} size={12} /> {cat.title}</>))}

          {/* Date the session was logged on — only meaningful for a logged
              session (targetDate is the day this modal's duration/time-log
              rows below are scoped to), not for a plain habit/task opened via
              "View details". */}
          {sessionToDelete && targetDate && metaRow(CalendarDays, "Date", new Date(targetDate + "T00:00:00").toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }))}

          {/* The linked habit/task/to-do no longer exists — say so plainly
              instead of showing frequency/times-per-day details we no longer
              have, since deleting it didn't keep that data around. */}
          {isDeletedLink && metaRow(Trash2, null, <span style={{ fontStyle: "italic" }}>
            This {kind === "habit" ? "habit" : kind === "task" ? "task" : "to-do"} has been deleted
          </span>)}

          {kind === "habit" && itemRef && ref.description && metaRow(Info, "Description", ref.description)}
          {kind === "habit" && itemRef && metaRow(Repeat, "Frequency", formatHabitFrequency(ref))}
          {kind === "habit" && itemRef && (isAmountHabit(dayGoal)
            ? metaRow(Check, "Goal", formatDailyGoal(dayGoal))
            : (ref.dailyCount || 1) > 1 && metaRow(Check, "Times per day", ref.dailyCount))}
          {/* What was actually logged that day for a unit habit ("10,523 steps").
              It's the day's total from habitLogs — the same number the Habits card
              shows — not a per-session amount, since amounts can also be added
              outside Clock-In and the panel already covers the whole day. */}
          {kind === "habit" && itemRef && habitLogs && targetDate && isAmountHabit(dayGoal)
            && metaRow(TrendingUp, "Completed", formatAmount(dayGoal, loggedAmount(dayGoal, habitLogs[targetDate]?.[ref.id])))}
          {kind === "habit" && itemRef && ref.endDate && metaRow(CalendarDays, "End date", ref.endDate)}

          {kind === "task" && itemRef && ref.dueDate && metaRow(Clock, "Due date", ref.dueTime ? `${ref.dueDate}, ${ref.dueTime}` : ref.dueDate)}
          {kind === "task" && itemRef && ref.time && metaRow(Clock, "Time", `${ref.time}${ref.endTime ? " – " + ref.endTime : ""}`)}
          {kind === "task" && itemRef && ref.location && metaRow(MapPin, "Location", ref.location)}
          {kind === "task" && itemRef && ref.priority && metaRow(Flag, "Priority", <span style={{ textTransform: "capitalize", color: PRIORITY_COLORS[ref.priority] || "var(--c-text-mid)", fontWeight: 800 }}>{ref.priority}</span>)}
          {kind === "task" && itemRef && formatTaskFrequency(ref) && metaRow(Repeat, "Frequency", formatTaskFrequency(ref))}

          {kind === "custom" && itemRef && ref.description && metaRow(Info, "Description", ref.description)}
          {kind === "custom" && itemRef && metaRow(Repeat, "Frequency", formatHabitFrequency(ref))}

          {/* Duration — total for the day + one line per individual log, only
              when it was actually run more than once that day; a single run
              just shows a plain duration + time log line. Hidden entirely when
              Settings ▸ Clock-In ▸ "Hide logged sessions duration" is on. */}
          {!hideDuration && dayLogs.length === 1 && metaRow(Timer, "Duration", (dayLogs[0].estimated ? editableTime(dayLogs[0], `${dayLogs[0].minutes} min`) : `${dayLogs[0].minutes} min`))}
          {!hideDuration && dayLogs.length === 1 && logTimeRangeLabel(dayLogs[0]) && metaRow(Clock, "Time log", editableTime(dayLogs[0], logTimeRangeLabel(dayLogs[0])))}
          {singleLog && noteRow(singleLog)}
          {notesOn && hideDuration && allDayLogs.length > 1 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {allDayLogs.map((s, i) => logLine(s, i, <span style={{ color: "var(--c-text-soft)", fontWeight: 700 }}>{logOrdinalLabel(i)} log</span>))}
            </div>
          )}
          {!hideDuration && dayLogs.length > 1 && (
            <div style={{ marginTop: 2 }}>
              {metaRow(Timer, "Total duration", `${totalMins} min`)}
              <div style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 6, marginLeft: 26 }}>
                {dayLogs.map((s, i) => {
                  const range = logTimeRangeLabel(s);
                  return logLine(s, i, <>
                    <span style={{ color: "var(--c-text-soft)", fontWeight: 700 }}>{logOrdinalLabel(i)} log: </span>
                    {editableTime(s, range ? `${range} (${s.minutes} min)` : `${s.minutes} min`)}
                  </>);
                })}
              </div>
            </div>
          )}

          {/* Sub-tasks — optional, fully independent of running the parent task itself. */}
          {kind === "task" && ref.subtasks?.length > 0 && (
            <div style={{ marginTop: 4 }}>
              <span style={{ fontSize: 11, fontWeight: 800, color: "var(--c-text-soft)", textTransform: "uppercase", letterSpacing: 0.8, display: "block", marginBottom: 6 }}>Sub-tasks</span>
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                {ref.subtasks.map(s => (
                  <div key={s.id} style={{ display: "flex", alignItems: "center", gap: 9, padding: "6px 10px", borderRadius: 10, background: "var(--c-bg-soft)" }}>
                    {/* Padded, isolated hit-zone — a slightly off tap on the small
                        checkbox used to land on the adjacent pencil button instead
                        (opening the rename field, and with it the keyboard). */}
                    <span style={{ padding: 6, margin: -6, display: "flex", flexShrink: 0 }} onClick={e => e.stopPropagation()}>
                      <HabitCheckbox checked={!!s.done} color="#d4789a" size={20} burst={false} onChange={() => toggleSubtask(ref.id, s.id)} />
                    </span>
                    {editingSubtaskId === s.id ? (
                      <input autoFocus value={editingSubtaskText} onChange={e => setEditingSubtaskText(e.target.value)}
                        onKeyDown={e => { if (e.key === "Enter") saveEditSubtask(ref.id); if (e.key === "Escape") setEditingSubtaskId(null); }}
                        onBlur={() => saveEditSubtask(ref.id)}
                        style={{ flex: 1, fontSize: 13, fontWeight: 600, color: "var(--c-text)", border: "1.5px solid var(--c-primary)", borderRadius: 8, padding: "4px 8px", background: "var(--c-bg-card)", fontFamily: "inherit" }} />
                    ) : (
                      <>
                        <span style={{ flex: 1, fontSize: 13, color: "var(--c-text-mid)", fontWeight: 600, textDecoration: s.done ? "line-through" : "none", opacity: s.done ? 0.55 : 1, wordBreak: "break-word" }}>{s.title}</span>
                        <button onClick={() => startEditSubtask(s)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--c-text-soft)", padding: 6, marginLeft: 6, display: "flex", alignItems: "center", alignSelf: "center", flexShrink: 0 }}><Pencil size={14} /></button>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {kind === "task" && ref.embeds?.length > 0 && (
            <div style={{ marginTop: 4 }}>
              <span style={{ fontSize: 11, fontWeight: 800, color: "var(--c-text-soft)", textTransform: "uppercase", letterSpacing: 0.8, display: "block", marginBottom: 6 }}>Embedded Links</span>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                {ref.embeds.map(em => <EmbedPill key={em.id} url={em.url} description={em.description} />)}
              </div>
            </div>
          )}

          {kind === "task" && ref.notes && (
            <div>
              <span style={{ fontSize: 11, fontWeight: 800, color: "var(--c-text-soft)", textTransform: "uppercase", letterSpacing: 0.8, display: "block", marginBottom: 6 }}>Notes</span>
              <div className="clarity-notes-view" style={{ fontSize: 13, color: "var(--c-text-mid)", background: "var(--c-bg-soft)", border: "1.5px solid var(--c-border)", borderRadius: 14, padding: "10px 14px", maxHeight: 120, overflowY: "auto", lineHeight: 1.5 }}
                dangerouslySetInnerHTML={{ __html: ref.notes }} />
            </div>
          )}

          {kind === "task" && Array.isArray(ref.attachments) && ref.attachments.length > 0 && (
            <div style={{ marginTop: 4 }}>
              <span style={{ fontSize: 11, fontWeight: 800, color: "var(--c-text-soft)", textTransform: "uppercase", letterSpacing: 0.8, display: "block", marginBottom: 6 }}><Paperclip size={11} style={{ marginRight: 4, verticalAlign: -1 }} />Attachments</span>
              <AttachmentViewer items={ref.attachments} />
            </div>
          )}
        </div>

          {/* Remove this specific logged session — only shown when the modal
              was opened from a logged session (donut ▸ category ▸ session),
              not from the plain "View details" / "Done" pill entry points. */}
          {sessionToDelete && onDeleteSession && (
            <button onClick={() => setConfirmDeleteSession(true)} title="Delete this session"
              style={{ background: "transparent", border: "none", width: 30, height: 30, cursor: "pointer", color: "var(--c-text-mid)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginBottom: -4 }}>
              <Trash2 size={15} />
            </button>
          )}
        </div>

        {/* Restart choice — only passed in when opened from the "Done" pill on
            the main clock-in card, right after finishing the active item today. */}
        {restart && (
          <div style={{ marginTop: 16, paddingTop: 14, borderTop: "1px solid var(--c-border)" }}>
            <p style={{ margin: "0 0 10px", fontSize: 12.5, color: "var(--c-text-soft)", lineHeight: 1.5 }}>
              {restart.autoCompleted
                ? "This ran its full duration already, so there's nothing left to continue. Restart it from 00:00?"
                : "Keep your timer where it left off, or start it over?"}
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {!restart.autoCompleted && (
                <button onClick={restart.onKeepTime}
                  style={{ width: "100%", padding: "11px 0", borderRadius: 12, border: "none", background: "linear-gradient(135deg, var(--c-primary-light), var(--c-primary-deep))", color: "#fff", fontFamily: "inherit", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
                  Continue and keep the time (paused)
                </button>
              )}
              <button onClick={restart.onRestart}
                style={{ width: "100%", padding: "11px 0", borderRadius: 12, border: restart.autoCompleted ? "none" : "1.5px solid var(--c-border-mid)", background: restart.autoCompleted ? "linear-gradient(135deg, var(--c-primary-light), var(--c-primary-deep))" : "var(--c-bg-card)", color: restart.autoCompleted ? "#fff" : "var(--c-text-mid)", fontFamily: "inherit", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
                Restart from 00:00
              </button>
            </div>
          </div>
        )}

      </div>

      {confirmDeleteSession && (
        <div style={{ position: "fixed", inset: 0, zIndex: 10060, display: "flex", alignItems: "center", justifyContent: "center", padding: 20, fontFamily: "'Quicksand','Nunito',sans-serif" }}>
          <div className="clarity-fade" style={{ position: "absolute", inset: 0, background: "rgba(40,20,35,0.5)", backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)" }} onClick={() => setConfirmDeleteSession(false)} />
          <div className="clarity-pop" style={{ position: "relative", background: "var(--c-bg-card)", borderRadius: 20, padding: "24px 22px 18px", maxWidth: 300, width: "100%", textAlign: "center", boxShadow: "0 10px 44px rgba(0,0,0,0.22)" }}>
            <ConfirmHeader title="Are you sure you want to remove this session?" />
            <div style={CONFIRM_STACK}>
              <button onClick={() => setConfirmDeleteSession(false)} style={CONFIRM_CANCEL}>Cancel</button>
              <button onClick={() => { onDeleteSession(sessionToDelete); setConfirmDeleteSession(false); onClose(); }}
                style={CONFIRM_DANGER}>Remove</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
